// color-exempt-file — pastilles aux couleurs des marques d'agenda (identification visuelle, pas du thème).
import React from "react";
import { useTranslation } from "react-i18next";
import { CalendarPlus, ChevronDown } from "lucide-react";
import { Button } from "@core/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@core/components/ui/dropdown-menu";
import {
  CALENDAR_TARGETS,
  buildCalendarUrl,
  downloadFinalDateIcs,
  type CalendarTarget,
  type FinalDateEvent,
} from "@core/lib/add-to-calendar";
import { trackFinalDate } from "@core/services/FinalDateService";

const BADGES: Record<CalendarTarget, { letter: string; bg: string }> = {
  apple: { letter: "", bg: "#111111" },
  google: { letter: "G", bg: "#4285F4" },
  office365: { letter: "O", bg: "#D83B01" },
  outlook: { letter: "O", bg: "#0078D4" },
  yahoo: { letter: "Y", bg: "#6001D2" },
};

const NAMES: Record<CalendarTarget, string> = {
  apple: "Apple",
  google: "Google",
  office365: "Office 365",
  outlook: "Outlook",
  yahoo: "Yahoo",
};

interface AddToCalendarMenuProps {
  event: FinalDateEvent;
  pollId?: string | null;
  /** Où le bouton est affiché, pour la mesure (page votant, résultats…). */
  source: string;
  className?: string;
  size?: "default" | "sm" | "lg";
}

/**
 * « Ajouter à mon calendrier » : Apple (.ics), Google, Office 365, Outlook,
 * Yahoo. Aucune connexion de compte : des liens de composition et un fichier.
 */
export const AddToCalendarMenu: React.FC<AddToCalendarMenuProps> = ({
  event,
  pollId,
  source,
  className,
  size = "default",
}) => {
  const { t } = useTranslation("polls");

  const open = (target: CalendarTarget) => {
    trackFinalDate("final_date_calendar_click", pollId, { target, source });
    if (target === "apple") {
      downloadFinalDateIcs(event);
      return;
    }
    window.open(buildCalendarUrl(target, event), "_blank", "noopener,noreferrer");
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button size={size} className={className} data-testid="add-to-calendar">
          <CalendarPlus className="h-4 w-4 mr-2" aria-hidden />
          {t("final_date.add_to_calendar")}
          <ChevronDown className="h-4 w-4 ml-2 opacity-70" aria-hidden />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="center" className="w-56">
        {CALENDAR_TARGETS.map((target) => (
          <DropdownMenuItem
            key={target}
            onSelect={() => open(target)}
            className="py-2.5 cursor-pointer"
            data-testid={`calendar-target-${target}`}
          >
            <span
              aria-hidden
              className="mr-3 inline-flex h-6 w-6 items-center justify-center rounded-md text-xs font-bold text-white"
              style={{ backgroundColor: BADGES[target].bg }}
            >
              {target === "apple" ? (
                <svg viewBox="0 0 24 24" className="h-3.5 w-3.5" fill="currentColor">
                  <path d="M16.37 12.6c-.02-2.2 1.8-3.26 1.88-3.31-1.03-1.5-2.62-1.7-3.18-1.72-1.35-.14-2.64.8-3.33.8-.69 0-1.74-.78-2.87-.76-1.47.02-2.83.86-3.59 2.18-1.53 2.66-.39 6.6 1.1 8.75.73 1.05 1.6 2.24 2.73 2.2 1.1-.05 1.51-.71 2.84-.71 1.32 0 1.7.71 2.86.69 1.18-.02 1.93-1.07 2.65-2.13.84-1.22 1.18-2.4 1.2-2.46-.03-.01-2.3-.88-2.32-3.5zM14.2 6.13c.6-.73 1.01-1.75.9-2.76-.87.04-1.92.58-2.54 1.31-.56.65-1.05 1.69-.92 2.68.97.08 1.96-.49 2.56-1.23z" />
                </svg>
              ) : (
                BADGES[target].letter
              )}
            </span>
            <span className="text-sm">{NAMES[target]}</span>
            {target === "apple" && (
              <span className="ml-auto text-xs text-muted-foreground">.ics</span>
            )}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default AddToCalendarMenu;
